/**
 * Images of the cold open, loaded and decoded before anything is revealed (no pop-in, no layout
 * shift): the wallpaper for the box orientation and the client avatar (poster for Jordan, message
 * request badge otherwise). `preloadColdOpen` can be called early (boot gate) and is idempotent.
 */
import { config } from '@/services/config';
import { imageMeta, loadManifest } from '@/services/media';
import { COLD_OPEN_WALLPAPERS } from './assets.generated';
import { clientAvatarAssetName } from './timeline';

export type WallpaperKind = keyof typeof COLD_OPEN_WALLPAPERS;

export function wallpaperFor(width: number, height: number) {
  const kind: WallpaperKind = width > height ? 'landscape' : 'portrait';
  const w = COLD_OPEN_WALLPAPERS[kind];
  return { kind, url: `${config.mediaBase}/${w.file}`, width: w.width, height: w.height };
}

/** Avatar URL from the media manifest (480 px variant: 136 pt poster at 3×), once the manifest is loaded. */
export function avatarURL(assetName: string | undefined): string | undefined {
  if (!assetName) return undefined;
  const entry = imageMeta(assetName);
  if (!entry) return undefined;
  return `${config.mediaBase}/${entry.file480 ?? entry.file}`;
}

const decoded = new Set<string>();
const pending = new Map<string, Promise<void>>();

/** True once `url` was decoded in this page (the component can then render synchronously). */
export function isDecoded(url: string | undefined): boolean {
  return !url || decoded.has(url);
}

export function decodeImage(url: string): Promise<void> {
  if (decoded.has(url)) return Promise.resolve();
  let p = pending.get(url);
  if (!p) {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    p = img.decode().catch(() => undefined).then(() => { decoded.add(url); });
    pending.set(url, p);
  }
  return p;
}

const withTimeout = (p: Promise<unknown>, ms: number) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]).then(() => undefined);

/**
 * Loads + decodes what the cold open of `episodeID` shows, for a box of `width`×`height`
 * (both wallpapers when the size is unknown). Resolves after `timeoutMs` at worst.
 */
export function preloadColdOpen(episodeID: string, size?: { width: number; height: number }, timeoutMs = 2500): Promise<void> {
  const wallpapers = size ? [wallpaperFor(size.width, size.height).url] : [wallpaperFor(1, 2).url, wallpaperFor(2, 1).url];
  // The other orientation warms up in the background (a rotation then swaps without a gap).
  if (size) void decodeImage(wallpaperFor(size.height, size.width).url);
  const name = clientAvatarAssetName(episodeID);
  const avatar = loadManifest().then(() => { const url = avatarURL(name); return url ? decodeImage(url) : undefined; }).catch(() => undefined);
  const fonts = typeof document !== 'undefined' && document.fonts ? document.fonts.ready : Promise.resolve();
  return withTimeout(Promise.all([...wallpapers.map(decodeImage), avatar, fonts]), timeoutMs);
}
